import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Loader2, ClipboardList } from "lucide-react";
import { Button } from "../ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { aiApi } from "./aiApi";
import AISchedules from "./AISchedules";

export default function AITasks({ user, onChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => aiApi.tasks().then(setData).catch(() => setData({ assigned_to_me: [], given_by_me: [] }));
  useEffect(() => { load(); }, []);
  const canGive = ["superadmin", "branch_manager", "admin"].includes(user.role);
  const canSchedule = ["superadmin", "branch_manager"].includes(user.role);
  const word = user.role === "ibu_bapa" ? "Makluman am" : "Tugasan";

  const done = async (t) => {
    setBusy(t.id);
    try { await aiApi.completeTask(t.id); toast.success(`${word} ditanda selesai`); load(); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setBusy(null); }
  };

  if (!data) return <div className="grid place-items-center h-40"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const groups = Object.values(data.given_by_me.reduce((acc, t) => {
    const g = acc[t.group_id] || (acc[t.group_id] = { ...t, total: 0, done: 0, names: [] });
    g.total += 1; if (t.status === "done") g.done += 1; g.names.push(t.assignee_name);
    return acc;
  }, {}));

  const Mine = () => (
    <div className="space-y-2">
      {data.assigned_to_me.length === 0 && <Empty text={`Tiada ${word.toLowerCase()} untuk anda.`} />}
      {data.assigned_to_me.map((t) => (
        <div key={t.id} className="rounded-2xl border border-slate-100 bg-white p-3 flex gap-2" data-testid="ai-task-item">
          {t.status === "done" ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className={`font-bold text-[13px] ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</div>
            {t.description && <div className="text-xs text-slate-500 whitespace-pre-wrap mt-0.5">{t.description}</div>}
            <div className="text-[11px] text-slate-400 mt-1">Daripada {t.created_by?.name}{t.due_date ? ` • akhir ${t.due_date}` : ""}</div>
          </div>
          {t.status !== "done" && (
            <Button size="sm" onClick={() => done(t)} disabled={busy === t.id} className="h-8 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="ai-task-complete">
              {busy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Selesai"}
            </Button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="h-full overflow-y-auto p-3" data-testid="ai-tasks">
      {!canGive ? <Mine /> : (
        <Tabs defaultValue="mine">
          <TabsList className={`w-full grid ${canSchedule ? "grid-cols-3" : "grid-cols-2"} h-9 rounded-xl`}>
            <TabsTrigger value="mine" className="rounded-lg text-xs">Untuk saya ({data.assigned_to_me.filter((t) => t.status === "pending").length})</TabsTrigger>
            <TabsTrigger value="given" className="rounded-lg text-xs" data-testid="ai-tasks-given-tab">Saya beri ({groups.length})</TabsTrigger>
            {canSchedule && <TabsTrigger value="sched" className="rounded-lg text-xs" data-testid="ai-tasks-schedules-tab">Berjadual</TabsTrigger>}
          </TabsList>
          <TabsContent value="mine" className="mt-3"><Mine /></TabsContent>
          {canSchedule && <TabsContent value="sched" className="mt-3"><AISchedules /></TabsContent>}
          <TabsContent value="given" className="mt-3 space-y-2">
            {groups.length === 0 && <Empty text="Minta AI: “Beri tugasan kepada ...” untuk mula." />}
            {groups.map((g) => (
              <div key={g.group_id} className="rounded-2xl border border-slate-100 bg-white p-3">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-[13px] text-slate-800 flex-1">{g.title}</div>
                  <Badge className={g.done === g.total ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{g.done}/{g.total} selesai</Badge>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(g.done / g.total) * 100}%` }} /></div>
                <div className="text-[11px] text-slate-400 mt-1.5 line-clamp-2">{g.names.join(", ")}{g.due_date ? ` • akhir ${g.due_date}` : ""}</div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="text-center py-10 text-slate-400 text-sm"><ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-50" />{text}</div>
);
